// Phase 100 — Projects > Task Registry: the "Deep Surface" master list of
// every task ever created (Active/Completed/Overdue), for audits, project
// reviews and workload visibility. Task Hub stays the "Fast Surface" for
// day-to-day add/check-off; this is where you go to dig in. Supersedes the
// earlier done-only "Task Archive".
import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search, ClipboardList } from "lucide-react";
import { PROJECT_CATEGORIES, PROJECT_CATEGORY_LABEL } from "@/lib/projectCategory";
import { formatLoggedHours } from "@/lib/dailyTaskStatus";

const ALL = "all";
const STATUS_OPTIONS = [
  { value: "overdue", label: "Overdue" },
  { value: "planned", label: "Planned" },
  { value: "in_progress", label: "In progress" },
  { value: "review", label: "Review" },
  { value: "done", label: "Done" },
  { value: "abandoned", label: "Abandoned" },
];
const STATUS_LABEL = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s.label]));
const STATUS_TONE = {
  overdue: "bg-red-50 text-red-700 border-red-200",
  planned: "bg-slate-50 text-slate-600 border-slate-200",
  in_progress: "bg-sky-50 text-sky-700 border-sky-200",
  review: "bg-purple-50 text-purple-700 border-purple-200",
  done: "bg-emerald-50 text-emerald-700 border-emerald-200",
  abandoned: "bg-rose-50 text-rose-700 border-rose-200",
};
const IMPORTANCE_OPTIONS = [
  { value: "high", label: "High" },
  { value: "medium", label: "Mid" },
  { value: "low", label: "Low" },
];
const todayIso = () => new Date().toISOString().slice(0, 10);

function effectiveStatus(t) {
  if (t.status !== "done" && t.status !== "abandoned" && t.due_date && t.due_date < todayIso()) return "overdue";
  return t.status || "planned";
}

export default function TaskRegistryTab() {
  const [tasks, setTasks] = useState(null);
  const [q, setQ] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState(ALL);
  const [projectFilter, setProjectFilter] = useState(ALL);
  const [statusFilter, setStatusFilter] = useState(ALL);
  const [priorityFilter, setPriorityFilter] = useState(ALL);

  useEffect(() => {
    api.get("/daily-tasks/registry")
      .then(({ data }) => setTasks(data || []))
      .catch((e) => { toast.error(formatApiError(e)); setTasks([]); });
  }, []);

  const assigneeOptions = useMemo(() => {
    const names = new Set((tasks || []).map((t) => t.assignee_name).filter(Boolean));
    return Array.from(names).sort();
  }, [tasks]);
  const projectOptions = useMemo(() => {
    const names = new Set((tasks || []).map((t) => t.project_name).filter(Boolean));
    return Array.from(names).sort();
  }, [tasks]);
  const hasUnlinkedTasks = useMemo(() => (tasks || []).some((t) => !t.project_name), [tasks]);

  const filtered = useMemo(() => {
    if (!tasks) return [];
    const needle = q.trim().toLowerCase();
    return tasks.filter((t) => {
      if (assigneeFilter !== ALL && t.assignee_name !== assigneeFilter) return false;
      if (projectFilter === "__none__" && t.project_name) return false;
      else if (projectFilter !== ALL && projectFilter !== "__none__" && t.project_name !== projectFilter) return false;
      if (statusFilter !== ALL && effectiveStatus(t) !== statusFilter) return false;
      if (priorityFilter !== ALL && (t.importance || "medium") !== priorityFilter) return false;
      if (needle && !`${t.title} ${t.project_name || ""} ${t.assignee_name || ""}`.toLowerCase().includes(needle)) return false;
      return true;
    });
  }, [tasks, q, assigneeFilter, projectFilter, statusFilter, priorityFilter]);

  return (
    <div data-testid="task-registry-tab">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search task, project or assignee…"
            className="rounded-sm pl-8 h-9 text-sm"
            data-testid="task-registry-search"
          />
        </div>
        <Select value={assigneeFilter} onValueChange={setAssigneeFilter}>
          <SelectTrigger className="h-9 rounded-sm text-sm w-auto max-w-[170px]" data-testid="task-registry-assignee-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All assignees</SelectItem>
            {assigneeOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={projectFilter} onValueChange={setProjectFilter}>
          <SelectTrigger className="h-9 rounded-sm text-sm w-auto max-w-[180px]" data-testid="task-registry-project-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All projects</SelectItem>
            {hasUnlinkedTasks && <SelectItem value="__none__">No project</SelectItem>}
            {projectOptions.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-9 rounded-sm text-sm w-auto max-w-[150px]" data-testid="task-registry-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={priorityFilter} onValueChange={setPriorityFilter}>
          <SelectTrigger className="h-9 rounded-sm text-sm w-auto max-w-[150px]" data-testid="task-registry-priority-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All importance</SelectItem>
            {IMPORTANCE_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500" data-testid="task-registry-count">{filtered.length} task{filtered.length === 1 ? "" : "s"}</span>
      </div>

      {tasks === null ? (
        <div className="text-sm text-slate-400 italic">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-flat p-8 text-center text-sm text-slate-400" data-testid="task-registry-empty">
          <ClipboardList size={22} className="mx-auto mb-2 text-slate-300" />
          No tasks match these filters.
        </div>
      ) : (
        <div className="card-flat overflow-x-auto">
          <table className="w-full text-sm min-w-[1020px]" data-testid="task-registry-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-[10px] uppercase tracking-wider text-slate-400">
                <th className="px-4 py-2.5 font-semibold">Task name</th>
                <th className="px-4 py-2.5 font-semibold">Project</th>
                <th className="px-4 py-2.5 font-semibold">Category</th>
                <th className="px-4 py-2.5 font-semibold">Assignee</th>
                <th className="px-4 py-2.5 font-semibold">Importance</th>
                <th className="px-4 py-2.5 font-semibold">Start date</th>
                <th className="px-4 py-2.5 font-semibold">Due date</th>
                <th className="px-4 py-2.5 font-semibold">Hours</th>
                <th className="px-4 py-2.5 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const st = effectiveStatus(t);
                return (
                  <tr key={t.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60" data-testid={`task-registry-row-${t.id}`}>
                    <td className="px-4 py-2.5 text-slate-800">{t.title}</td>
                    <td className="px-4 py-2.5 text-slate-600">{t.project_name || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600">{PROJECT_CATEGORY_LABEL[t.category] || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600">{t.assignee_name || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600 capitalize">{t.importance || "medium"}</td>
                    <td className="px-4 py-2.5 text-slate-600">{(t.start_date || t.created_at) ? new Date(t.start_date || t.created_at).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600">{t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}</td>
                    <td className="px-4 py-2.5 text-slate-500 text-xs">{formatLoggedHours(t.total_minutes)}</td>
                    <td className="px-4 py-2.5">
                      <span className={`inline-block px-2 py-0.5 rounded-full text-[11px] font-medium border ${STATUS_TONE[st]}`}>{STATUS_LABEL[st]}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
